// src/components/AutoLogoutProvider.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { fetchWrapper } from "utils/apiFetch";
import { showMainToast } from "utils/showToast";

const TIMEOUT_MS = 5 * 60 * 60 * 1000; // 5 hours
const WARNING_MS = 60 * 1000;          // Show warning for last 60 seconds
const WARN_AT_MS = TIMEOUT_MS - WARNING_MS; // Warn after 4 hours 59 minutes

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "click",
];

export default function AutoLogoutProvider({ children }) {
  const idleTimer    = useRef(null);
  const warnTimer    = useRef(null);
  const countdownRef = useRef(null);

  const [showWarning, setShowWarning] = useState(false);
  const [countdown,   setCountdown]   = useState(60);

  // ── Clear all running timers ─────────────────────────────────────────────
  const clearAllTimers = () => {
    clearTimeout(idleTimer.current);
    clearTimeout(warnTimer.current);
    clearInterval(countdownRef.current);
  };

  // ── Logout — your exact logic ────────────────────────────────────────────
  const logout = useCallback(async () => {
    clearAllTimers();
    setShowWarning(false);

    try {
      await fetchWrapper("https://pro.mokshiq.in/logout_api", {});
    } catch (error) {
      console.log("Logout API Error:", error);
    }

    localStorage.clear();
    sessionStorage.clear();

    showMainToast("Log out successfully", "success");

    window.location.replace("/");
  }, []);

  // ── Start the 60-second warning countdown ───────────────────────────────
  const startWarningCountdown = useCallback(() => {
    setShowWarning(true);
    setCountdown(60);

    // Tick every second
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Auto logout after warning period
    idleTimer.current = setTimeout(logout, WARNING_MS);
  }, [logout]);

  // ── Reset all timers (called on any user activity) ───────────────────────
  const resetTimer = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setCountdown(60);
    // Start fresh: warn after WARN_AT_MS (4 min)
    warnTimer.current = setTimeout(startWarningCountdown, WARN_AT_MS);
  }, [startWarningCountdown]);

  // ── Mount: attach listeners + start timer ───────────────────────────────
  useEffect(() => {
    resetTimer();
    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, resetTimer, { passive: true })
    );
    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((e) =>
        window.removeEventListener(e, resetTimer)
      );
    };
  }, [resetTimer]);

  return (
    <>
      {children}

      {/* ── Warning Modal ─────────────────────────────────────────────── */}
      {showWarning && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            className="card shadow-lg"
            style={{ width: 400, borderRadius: 14, overflow: "hidden" }}
          >
            {/* Header */}
            <div
              className="d-flex align-items-center gap-2 px-4 py-3"
              style={{ background: "#fff3cd", borderBottom: "1px solid #ffe69c" }}
            >
              <i
                className="bi bi-exclamation-triangle-fill text-warning"
                style={{ fontSize: 20 }}
              />
              <span className="fw-bold" style={{ fontSize: 15, color: "#664d03" }}>
                Session Expiring Soon
              </span>
            </div>

            {/* Body */}
            <div className="card-body text-center py-4 px-4">
              {/* Countdown circle */}
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
                style={{
                  width: 80,
                  height: 80,
                  background: countdown <= 10 ? "#fee2e2" : "#fff3cd",
                  border: `3px solid ${countdown <= 10 ? "#f87171" : "#fbbf24"}`,
                  transition: "all 0.3s",
                }}
              >
                <span
                  className="fw-bold"
                  style={{
                    fontSize: 30,
                    color: countdown <= 10 ? "#dc2626" : "#d97706",
                    lineHeight: 1,
                  }}
                >
                  {countdown}
                </span>
              </div>

              <p className="mb-1 fw-semibold" style={{ fontSize: 15 }}>
                You've been inactive for a while.
              </p>
              <p className="text-muted small mb-0">
                You will be automatically logged out in{" "}
                <strong>
                  {countdown} second{countdown !== 1 ? "s" : ""}
                </strong>{" "}
                due to inactivity.
              </p>
            </div>

            {/* Footer */}
            <div
              className="d-flex gap-2 justify-content-center pb-4 px-4"
              style={{ borderTop: "none" }}
            >
              <button
                className="btn btn-primary px-4"
                style={{ borderRadius: 8 }}
                onClick={resetTimer}
              >
                <i className="bi bi-arrow-repeat me-2" />
                Stay Logged In
              </button>
              <button
                className="btn btn-outline-danger px-4"
                style={{ borderRadius: 8 }}
                onClick={logout}
              >
                <i className="bi bi-box-arrow-right me-2" />
                Logout Now
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}